import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid2 as Grid, TextField, MenuItem,
  Paper, Tabs, Tab, Chip, CircularProgress, Alert, Snackbar, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
} from '@mui/material';
import {
  SmartToy as BotIcon, Send as SendIcon, Save as SaveIcon, Refresh as RefreshIcon,
  Chat as ChatIcon, Settings as ConfigIcon, History as HistoryIcon,
  Assessment as StatsIcon, Person as UserIcon, Close as CloseIcon,
  RestartAlt as ResetIcon, MenuBook as KbIcon, Add as AddIcon,
  Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  SpeedOutlined as SeedIcon, PlayArrow as TestIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { useTranslation } from 'react-i18next';

const formatLyraText = (text: string): string => {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    .replace(/^#{1,4}\s+/gm, '')
    .replace(/^[-*]\s+/gm, '• ')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`(.*?)`/g, '$1')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  provider?: string;
}

interface LyraConfig {
  systemPrompt: string;
  model: string;
  provider: string;
  maxTokens: number;
  temperature: number;
  active: string;
}

interface Session {
  session_id: string;
  user_id: string;
  started_at: string;
  last_message_at: string;
  message_count: number;
  first_message: string;
  /** web (site), app (Pagali) ou whatsapp (999 91 49) — a mesma Lyra nos três */
  canal?: 'web' | 'app' | 'whatsapp';
  /** o número, nas conversas do WhatsApp */
  contacto?: string | null;
  nome_cliente?: string | null;
  /** a conversa foi encaminhada para uma pessoa */
  humano?: boolean;
}

interface KbArticle {
  id: number;
  titulo: string;
  categoria: string;
  tags: string;
  preview?: string;
  conteudo?: string;
  ativo: number;
  updated_at: string;
}

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

const LyraAdmin: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Chat state
  const [messages, setMessages] = useState<Message[]>([
    { id: '1', text: 'Olá! Sou a Lyra. Testa-me aqui antes de publicar.', sender: 'assistant', timestamp: new Date() },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `admin-test-${Date.now()}`);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Config state
  const [config, setConfig] = useState<LyraConfig>({
    systemPrompt: '', model: 'claude-sonnet-4-20250514', provider: 'anthropic', maxTokens: 500, temperature: 0.7, active: 'S',
  });
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [resettingConfig, setResettingConfig] = useState(false);

  // Stats state
  const [stats, setStats] = useState<any>(null);

  // Knowledge Base state
  const [kbArticles, setKbArticles] = useState<KbArticle[]>([]);
  const [loadingKb, setLoadingKb] = useState(false);
  const [kbSearch, setKbSearch] = useState('');
  const [kbDialog, setKbDialog] = useState<{ open: boolean; article: Partial<KbArticle> | null }>({ open: false, article: null });
  const [savingKb, setSavingKb] = useState(false);
  const [kbTestQuery, setKbTestQuery] = useState('');
  const [kbTestResult, setKbTestResult] = useState<string | null>(null);
  const [testingKb, setTestingKb] = useState(false);
  const [seedingKb, setSeedingKb] = useState(false);

  // Conversations state
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [sessionMessages, setSessionMessages] = useState<any[]>([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(0);
  const [canalFiltro, setCanalFiltro] = useState<'' | 'web' | 'app' | 'whatsapp'>('');

  const showToast = (message: string, severity: 'success' | 'error') => setToast({ open: true, message, severity });

  // Load config
  const fetchConfig = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const res = await api.get('/admin/lyra/config');
      if (res.data.success) setConfig(res.data.data);
    } catch { /* ignore */ }
    finally { setLoadingConfig(false); }
  }, []);

  // Load stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/lyra/stats');
      if (res.data.success) setStats(res.data.data);
    } catch { /* ignore */ }
  }, []);

  // Load conversations
  const fetchSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const res = await api.get(`/admin/lyra/conversations?limit=20&offset=${sessionsPage * 20}${canalFiltro ? `&canal=${canalFiltro}` : ''}`);
      if (res.data.success) {
        setSessions(res.data.data);
        setSessionsTotal(res.data.total);
      }
    } catch { /* ignore */ }
    finally { setLoadingSessions(false); }
  }, [sessionsPage, canalFiltro]);

  // Load KB articles
  const fetchKb = useCallback(async () => {
    setLoadingKb(true);
    try {
      const params = kbSearch ? `?search=${encodeURIComponent(kbSearch)}` : '';
      const res = await api.get(`/admin/lyra/knowledge${params}`);
      if (res.data.success) setKbArticles(res.data.data);
    } catch { /* ignore */ }
    finally { setLoadingKb(false); }
  }, [kbSearch]);

  useEffect(() => { fetchConfig(); fetchStats(); }, [fetchConfig, fetchStats]);
  useEffect(() => { if (tab === 2) fetchSessions(); }, [tab, fetchSessions]);
  useEffect(() => { if (tab === 3) fetchKb(); }, [tab, fetchKb]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // KB handlers
  const handleKbSave = async () => {
    if (!kbDialog.article?.titulo || !kbDialog.article?.conteudo) return;
    setSavingKb(true);
    try {
      if (kbDialog.article.id) {
        await api.put(`/admin/lyra/knowledge/${kbDialog.article.id}`, kbDialog.article);
      } else {
        await api.post('/admin/lyra/knowledge', kbDialog.article);
      }
      showToast(kbDialog.article.id ? 'Artigo actualizado' : 'Artigo criado', 'success');
      setKbDialog({ open: false, article: null });
      fetchKb();
    } catch { showToast('Erro ao guardar artigo', 'error'); }
    finally { setSavingKb(false); }
  };

  const handleKbDelete = async (id: number) => {
    if (!window.confirm('Eliminar este artigo da base de conhecimento?')) return;
    try {
      await api.delete(`/admin/lyra/knowledge/${id}`);
      showToast('Artigo eliminado', 'success');
      fetchKb();
    } catch { showToast('Erro ao eliminar', 'error'); }
  };

  const handleKbTest = async () => {
    if (!kbTestQuery.trim()) return;
    setTestingKb(true);
    setKbTestResult(null);
    try {
      const res = await api.post('/admin/lyra/knowledge/test', { query: kbTestQuery });
      setKbTestResult(res.data.hasResults ? res.data.context : 'Nenhum artigo encontrado para esta pergunta.');
    } catch { setKbTestResult('Erro ao testar.'); }
    finally { setTestingKb(false); }
  };

  const handleKbSeed = async () => {
    if (!window.confirm('Carregar todos os artigos Pagali? Os artigos existentes serão substituídos.')) return;
    setSeedingKb(true);
    try {
      const res = await api.post('/admin/lyra/knowledge/seed', { replace: true });
      showToast(res.data.message, 'success');
      fetchKb();
    } catch { showToast('Erro ao carregar artigos', 'error'); }
    finally { setSeedingKb(false); }
  };

  // Chat
  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg: Message = { id: Date.now().toString(), text: input.trim(), sender: 'user', timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const res = await api.post('/public/lyra/chat', { message: userMsg.text, sessionId });
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        text: res.data.response || 'Sem resposta',
        sender: 'assistant',
        timestamp: new Date(),
        provider: res.data.provider,
      }]);
    } catch {
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(), text: 'Erro ao comunicar com a Lyra.', sender: 'assistant', timestamp: new Date(),
      }]);
    } finally { setSending(false); }
  };

  // Reset to built-in default prompt
  const handleResetToDefault = async () => {
    if (!window.confirm('Repor o system prompt para o padrão mais recente? O prompt actual será substituído.')) return;
    setResettingConfig(true);
    try {
      const res = await api.post('/admin/lyra/config/reset');
      if (res.data.success) {
        setConfig(prev => ({ ...prev, systemPrompt: res.data.systemPrompt }));
        showToast('Prompt reposto para o padrão mais recente', 'success');
      }
    } catch {
      showToast('Erro ao repor configuração', 'error');
    } finally { setResettingConfig(false); }
  };

  // Save config
  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/admin/lyra/config', config);
      showToast('Configuração guardada com sucesso', 'success');
    } catch {
      showToast('Erro ao guardar configuração', 'error');
    } finally { setSavingConfig(false); }
  };

  // View session messages
  const handleViewSession = async (sid: string) => {
    setSelectedSession(sid);
    try {
      const res = await api.get(`/admin/lyra/conversations/${sid}`);
      if (res.data.success) setSessionMessages(res.data.data);
    } catch { /* ignore */ }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BotIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>Lyra AI</Typography>
            <Typography variant="body2" color="text.secondary">Assistente Virtual Pagali — Gestão e Configuração</Typography>
          </Box>
        </Box>
        <Chip
          label={config.active === 'S' ? 'Activa' : 'Inactiva'}
          color={config.active === 'S' ? 'success' : 'default'}
          icon={<BotIcon />}
        />
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card><CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChatIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.totalSessions || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Conversas</Typography>
                </Box>
              </Box>
            </CardContent></Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card><CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SendIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.totalMessages || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Mensagens</Typography>
                </Box>
              </Box>
            </CardContent></Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card><CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StatsIcon color="warning" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.last24h || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Últimas 24h</Typography>
                </Box>
              </Box>
            </CardContent></Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card><CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.last7days || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Últimos 7 dias</Typography>
                </Box>
              </Box>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<ChatIcon />} iconPosition="start" label="Testar Chat" />
          <Tab icon={<ConfigIcon />} iconPosition="start" label="Configuração" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Conversas" />
          <Tab icon={<KbIcon />} iconPosition="start" label="Base de Conhecimento" />
        </Tabs>
      </Paper>

      {/* Tab 0: Chat Test */}
      <TabPanel value={tab} index={0}>
        <Paper sx={{ height: 500, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Messages */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'action.hover' }}>
            {messages.map((msg) => (
              <Box key={msg.id} sx={{ display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start', mb: 1.5 }}>
                <Box sx={{
                  maxWidth: '75%', px: 2, py: 1, borderRadius: 2,
                  bgcolor: msg.sender === 'user' ? 'primary.main' : 'white',
                  color: msg.sender === 'user' ? 'white' : 'text.primary',
                  boxShadow: msg.sender === 'assistant' ? 1 : 0,
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.sender === 'assistant' ? formatLyraText(msg.text) : msg.text}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      {msg.timestamp.toLocaleTimeString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    {msg.provider && (
                      <Chip label={msg.provider} size="small" variant="outlined" sx={{ height: 16, fontSize: 10, ml: 1 }} />
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
            {sending && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <Box sx={{ bgcolor: 'background.paper', px: 2, py: 1.5, borderRadius: 2, boxShadow: 1 }}>
                  <CircularProgress size={16} />
                </Box>
              </Box>
            )}
            <div ref={chatEndRef} />
          </Box>
          {/* Input */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
            <TextField
              fullWidth size="small" placeholder="Escreve uma mensagem para testar..."
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              disabled={sending}
            />
            <Button variant="contained" onClick={handleSend} disabled={!input.trim() || sending} startIcon={<SendIcon />}>
              Enviar
            </Button>
          </Box>
        </Paper>
      </TabPanel>

      {/* Tab 1: Configuration */}
      <TabPanel value={tab} index={1}>
        {loadingConfig ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={3}>
            {/* LLM Settings */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Modelo LLM</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField fullWidth size="small" select label="Provider"
                        value={config.provider} onChange={(e) => setConfig({ ...config, provider: e.target.value })}>
                        <MenuItem value="anthropic">Anthropic (Claude)</MenuItem>
                        <MenuItem value="openai">OpenAI (GPT)</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField fullWidth size="small" select label="Modelo"
                        value={config.model} onChange={(e) => setConfig({ ...config, model: e.target.value })}>
                        {config.provider === 'anthropic' ? (
                          [
                            <MenuItem key="opus" value="claude-opus-4-20250514">Claude Opus 4</MenuItem>,
                            <MenuItem key="sonnet" value="claude-sonnet-4-20250514">Claude Sonnet 4</MenuItem>,
                            <MenuItem key="haiku" value="claude-haiku-4-5-20251001">Claude Haiku 4.5</MenuItem>,
                          ]
                        ) : (
                          [
                            <MenuItem key="gpt4o" value="gpt-4o">GPT-4o</MenuItem>,
                            <MenuItem key="gpt4omini" value="gpt-4o-mini">GPT-4o Mini</MenuItem>,
                            <MenuItem key="gpt4turbo" value="gpt-4-turbo">GPT-4 Turbo</MenuItem>,
                          ]
                        )}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField fullWidth size="small" type="number" label="Max Tokens"
                        value={config.maxTokens} onChange={(e) => setConfig({ ...config, maxTokens: parseInt(e.target.value) || 500 })}
                        inputProps={{ min: 100, max: 4000 }} />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField fullWidth size="small" type="number" label="Temperatura"
                        value={config.temperature} onChange={(e) => setConfig({ ...config, temperature: parseFloat(e.target.value) || 0.7 })}
                        inputProps={{ min: 0, max: 1, step: 0.1 }} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField fullWidth size="small" select label="Estado"
                        value={config.active} onChange={(e) => setConfig({ ...config, active: e.target.value })}>
                        <MenuItem value="S">Activa</MenuItem>
                        <MenuItem value="N">Inactiva</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* System Prompt */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>System Prompt</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {config.systemPrompt.length} caracteres
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth multiline rows={18}
                    value={config.systemPrompt}
                    onChange={(e) => setConfig({ ...config, systemPrompt: e.target.value })}
                    placeholder="Define a personalidade, expertise e regras da Lyra..."
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 } }}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Save Button */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" color="warning" startIcon={resettingConfig ? <CircularProgress size={16} /> : <ResetIcon />}
                  onClick={handleResetToDefault} disabled={resettingConfig || savingConfig}>
                  {resettingConfig ? 'A repor...' : 'Repor Padrão'}
                </Button>
                <Button variant="contained" startIcon={savingConfig ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSaveConfig} disabled={savingConfig || resettingConfig}>
                  {savingConfig ? 'A guardar...' : 'Guardar Configuração'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Tab 2: Conversations */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            select size="small" label="Canal" value={canalFiltro} sx={{ minWidth: 200 }}
            onChange={(e) => { setCanalFiltro(e.target.value as any); setSessionsPage(0); }}
          >
            <MenuItem value="">Todos os canais</MenuItem>
            <MenuItem value="web">Site pagali.cv</MenuItem>
            <MenuItem value="app">App Pagali</MenuItem>
            <MenuItem value="whatsapp">WhatsApp suporte (999 91 49)</MenuItem>
          </TextField>
          <Typography variant="caption" color="text.secondary">
            A mesma Lyra de suporte responde nos três canais.
          </Typography>
        </Box>
        <Paper>
          {loadingSessions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={130}>Canal</TableCell>
                      <TableCell>Primeira Mensagem</TableCell>
                      <TableCell width={150}>Contacto</TableCell>
                      <TableCell width={110}>Mensagens</TableCell>
                      <TableCell width={160}>Início</TableCell>
                      <TableCell width={160}>Última</TableCell>
                      <TableCell width={80} align="center">Ver</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">Nenhuma conversa encontrada</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sessions.map((s) => (
                        <TableRow key={s.session_id} hover>
                          <TableCell>
                            <Chip
                              size="small"
                              label={s.canal === 'whatsapp' ? 'WhatsApp' : s.canal === 'app' ? 'App' : 'Site'}
                              color={s.canal === 'whatsapp' ? 'success' : s.canal === 'app' ? 'info' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 360 }}>
                              {s.first_message || '-'}
                            </Typography>
                            {s.humano && (
                              <Chip size="small" color="warning" variant="outlined" label="passou a uma pessoa" sx={{ mt: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {s.contacto ? `+${s.contacto}` : s.nome_cliente || (s.user_id ? 'com sessão' : '-')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={s.message_count} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {s.started_at ? new Date(s.started_at).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {s.last_message_at ? new Date(s.last_message_at).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => handleViewSession(s.session_id)}>
                              <ChatIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div" count={sessionsTotal} page={sessionsPage}
                onPageChange={(_, p) => setSessionsPage(p)} rowsPerPage={20}
                rowsPerPageOptions={[20]}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </>
          )}
        </Paper>
      </TabPanel>

      {/* Tab 3: Knowledge Base */}
      <TabPanel value={tab} index={3}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small" placeholder="Pesquisar artigos..." value={kbSearch}
            onChange={e => setKbSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchKb()}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Button variant="outlined" onClick={fetchKb} startIcon={<RefreshIcon />} size="small">Actualizar</Button>
          <Button variant="outlined" color="warning" size="small"
            startIcon={seedingKb ? <CircularProgress size={14} /> : <AddIcon />}
            onClick={handleKbSeed} disabled={seedingKb}>
            Carregar Exemplos
          </Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => setKbDialog({ open: true, article: { titulo: '', categoria: 'pagamentos', tags: '', conteudo: '', ativo: 1 } })}>
            Novo Artigo
          </Button>
        </Box>

        {/* Test section */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Testar pesquisa da KB</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" fullWidth placeholder="Ex: como pagar a luz?" value={kbTestQuery}
              onChange={e => setKbTestQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleKbTest()} />
            <Button variant="contained" size="small" onClick={handleKbTest} disabled={testingKb}
              startIcon={testingKb ? <CircularProgress size={14} /> : <TestIcon />}>
              Testar
            </Button>
          </Box>
          {kbTestResult && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11 }}>
                {kbTestResult}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Articles table */}
        {loadingKb ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    <TableCell width={120}>Categoria</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell width={80}>Estado</TableCell>
                    <TableCell width={100} align="center">Acções</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kbArticles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          Nenhum artigo. Clica "Carregar Exemplos" para começar.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : kbArticles.map(a => (
                    <TableRow key={a.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{a.titulo}</Typography>
                        {a.preview && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 400 }}>{a.preview}…</Typography>}
                      </TableCell>
                      <TableCell><Chip label={a.categoria} size="small" variant="outlined" /></TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{a.tags}</Typography></TableCell>
                      <TableCell>
                        <Chip label={a.ativo ? 'Activo' : 'Inactivo'} size="small" color={a.ativo ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={async () => {
                          const res = await api.get(`/admin/lyra/knowledge/${a.id}`);
                          setKbDialog({ open: true, article: res.data.data });
                        }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleKbDelete(a.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">{kbArticles.length} artigos</Typography>
            </Box>
          </Paper>
        )}
      </TabPanel>

      {/* KB Article Dialog */}
      <Dialog open={kbDialog.open} onClose={() => setKbDialog({ open: false, article: null })} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {kbDialog.article?.id ? 'Editar Artigo' : 'Novo Artigo'}
          <IconButton onClick={() => setKbDialog({ open: false, article: null })}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField fullWidth size="small" label="Título *" value={kbDialog.article?.titulo || ''}
              onChange={e => setKbDialog(d => ({ ...d, article: { ...d.article!, titulo: e.target.value } }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth size="small" label="Categoria" value={kbDialog.article?.categoria || 'geral'}
                onChange={e => setKbDialog(d => ({ ...d, article: { ...d.article!, categoria: e.target.value } }))}
                helperText="Ex: pagamentos, wallet, fidelidade, eventos, empresas, poupança" />
              <TextField fullWidth size="small" label="Estado" select value={kbDialog.article?.ativo ?? 1}
                onChange={e => setKbDialog(d => ({ ...d, article: { ...d.article!, ativo: Number(e.target.value) } }))}>
                <MenuItem value={1}>Activo</MenuItem>
                <MenuItem value={0}>Inactivo</MenuItem>
              </TextField>
            </Box>
            <TextField fullWidth size="small" label="Tags (palavras-chave separadas por espaço)"
              value={kbDialog.article?.tags || ''}
              onChange={e => setKbDialog(d => ({ ...d, article: { ...d.article!, tags: e.target.value } }))}
              helperText="Ex: electricidade electra luz pagar conta" />
            <TextField fullWidth multiline rows={16} label="Conteúdo do artigo (passo a passo) *"
              value={kbDialog.article?.conteudo || ''}
              onChange={e => setKbDialog(d => ({ ...d, article: { ...d.article!, conteudo: e.target.value } }))}
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 } }}
              placeholder="Descreve o fluxo passo a passo exactamente como aparece na app..." />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKbDialog({ open: false, article: null })}>Cancelar</Button>
          <Button variant="contained" onClick={handleKbSave} disabled={savingKb}
            startIcon={savingKb ? <CircularProgress size={14} /> : <SaveIcon />}>
            {savingKb ? 'A guardar...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session Messages Dialog */}
      <Dialog open={!!selectedSession} onClose={() => setSelectedSession(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon /> Conversa
          </Box>
          <IconButton onClick={() => setSelectedSession(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {sessionMessages.map((msg: any, i: number) => (
              <Box key={i} sx={{
                display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <Box sx={{
                  maxWidth: '80%', px: 2, py: 1, borderRadius: 2,
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'action.hover',
                  color: msg.role === 'user' ? 'white' : 'text.primary',
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.role === 'assistant' ? formatLyraText(msg.message) : msg.message}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : ''}
                    </Typography>
                    {msg.provider && <Chip label={msg.provider} size="small" sx={{ height: 14, fontSize: 9 }} />}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedSession(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LyraAdmin;
